import { z } from 'zod'
import { idParam } from './id-validators'
// batch-62 briefpapier (FO-459) - de briefpapierkeuze bij het genereren.
import { BriefpapierKeuzeInvoerSchema } from './briefpapier'
// batch-57 overeenkomst-vervolg (FO-415 §6.4) — open bruikleen-taken schuiven
// de offboarding-cockpit in.
import type { BruikleenTriggerDTO } from './overeenkomst-vervolg'
// batch-58 uitdienst-verlof (FO-424) — de verlof-stap hoort bij de uitdienst-regie
// en staat daarom in de cockpit.
import type { OffboardingVerlofStapDTO } from './uitdienst-verlof'
// batch-67 middelen-inname, fase B (FO-505) - een inlever-regel kan een reparatieverzoek dragen.
import { bedragTekst, type ReparatieVerzoekStatus } from './reparatie'

/**
 * FO-307-A5 (batch-33) - offboarding-spiegel voor de portal ("Uit dienst - in te
 * leveren"). Read-only weergave; nooit bedragen of interne notities.
 *
 * FO-396 (batch-46) - de portal-spiegel wordt nu gevoed door de
 * OffboardingWorkflow (besluit 7). De cockpit-DTO's + actie-schemas hieronder
 * horen bij de backoffice-cockpit ("Offboarding"-tab op /medewerkers/[id]).
 */
export type OffboardingItemSoort =
  | 'VOERTUIG'
  | 'TANKPAS'
  | 'UNIEK_ITEM'
  | 'UITRUSTING'
  | 'RETOURPLICHT'
  | 'ABONNEMENT'
  // batch-67 middelen-inname (FO-505 §A1) - kleding, PBM en gereedschap dat als
  // bulk uit de voorraad is uitgegeven (netto uitgifte min retour per artikel).
  | 'BULK'

export interface OffboardingItemDTO {
  soort: OffboardingItemSoort
  label: string
  /** FO-396 - status van de inname-regel (open vs afgehandeld). Portal-veilig. */
  status?: 'OPEN' | 'AFGEHANDELD'
}

export interface MijnOffboardingDTO {
  /** true zodra HR de uitdienst-workflow heeft gestart (einddatum gezet). */
  actief: boolean
  uitDienstDatum: string | null
  items: OffboardingItemDTO[]
  /**
   * batch-61 offboarding-afronding (FO-451) - de inleverafspraak uit de laatste
   * verstuurde inleverbrief en de aan de medewerker vrijgegeven documenten
   * (brief, inname-bevestiging, eindafrekening). Portal-veilig: alleen titels
   * en document-id's, nooit bedragen of interne notities.
   */
  inleverafspraak?: MijnOffboardingInleverafspraakDTO | null
  documenten?: MijnOffboardingDocumentDTO[]
}

export interface MijnOffboardingInleverafspraakDTO {
  /** ISO-datum (yyyy-mm-dd). */
  inleverDatum: string | null
  inleverPlek: string | null
  inleverPersoon: string | null
}

export interface MijnOffboardingDocumentDTO {
  documentId: string
  titel: string
  soort: OffboardingDocumentSoort
  versieNr: number
  /** ISO-datetime van de vrijgave/verzending. */
  verstuurdOp: string | null
}

// ─── Cockpit (backoffice) ─────────────────────────────────────────────────────

export const OFFBOARDING_WORKFLOW_STATUS = ['LOPEND', 'AFGEROND', 'GEANNULEERD'] as const
export type OffboardingWorkflowStatus = (typeof OFFBOARDING_WORKFLOW_STATUS)[number]

export const OFFBOARDING_INNAME_MODUS = ['INNEMEN', 'AFBOEKEN', 'OVERNEMEN', 'AL_INGENOMEN'] as const
export type OffboardingInnameModus = (typeof OFFBOARDING_INNAME_MODUS)[number]

/**
 * batch-71 offboarding-inname (FO-535 §A4) - wat `POST .../inname/afhandelen` accepteert.
 * AL_INGENOMEN heeft een eigen route met preview en bulk (`POST .../inname/al-ingenomen`).
 */
export const OFFBOARDING_INNAME_MODUS_AFHANDELEN = ['INNEMEN', 'AFBOEKEN', 'OVERNEMEN'] as const

export const OFFBOARDING_INNAME_MODUS_LABELS: Record<OffboardingInnameModus, string> = {
  INNEMEN: 'Innemen',
  AFBOEKEN: 'Afboeken',
  OVERNEMEN: 'Overnemen tegen restwaarde',
  AL_INGENOMEN: 'Al ingenomen',
}

/**
 * batch-71 offboarding-inname (FO-535 §8) - hoe een regel op de inleverlijst is
 * afgehandeld (spiegelt de Prisma-enum `OffboardingAfhandelingBron`).
 */
export const OFFBOARDING_AFHANDELING_BRONNEN = ['COCKPIT', 'INNAME_SESSIE', 'LEDGER', 'AL_INGENOMEN', 'OVERSLAG'] as const
export type OffboardingAfhandelingBron = (typeof OFFBOARDING_AFHANDELING_BRONNEN)[number]

/** Eén inname-regel uit de systeemdata (voertuig/tankpas/uniek item/uitrusting/...). */
export interface OffboardingInnameRegelDTO {
  /** Deterministische sleutel "INNAME:<soort>:<id>" (= OperationeleTaak.bronSubKey). */
  bronSubKey: string
  soort: OffboardingItemSoort
  label: string
  detail: string | null
  /** true zodra het item is ingenomen/afgeboekt/overgenomen (bron-mutatie of afhandeling). */
  afgehandeld: boolean
  /** Gekozen afhandeling (null zolang open). */
  modus: OffboardingInnameModus | null
  /** Restwaarde (Decimal-string, alleen bij OVERNEMEN). Backoffice-only. */
  restwaarde: string | null
  reden: string | null
  /** Innemen mogelijk via het bestaande inname-pad (uniek/retourplicht/uitrusting/voertuig). */
  kanInnemen: boolean
  /** Afboeken (met reden) mogelijk via het bestaande afschrijf-pad. */
  kanAfboeken: boolean
  /** Servicekoffer/drager: inname vereist een afgeronde inhoudscontrole (T5). */
  vereistInhoudscontrole: boolean
  inhoudscontroleGereed: boolean
  /** Voor het innemen van een uniek/retourplicht-item is een doellocatie nodig. */
  vereistLocatie: boolean
  taakStatus: string | null
  // batch-67 middelen-inname (FO-499 §C8 / FO-505 §A1) — de uitgifte volgens het
  // middelen-ledger. Leeg bij bezit van vóór het ledger.
  /** ISO-datetime van de uitgifte of overdracht aan deze medewerker. */
  uitgegevenOp?: string | null
  /** Document-id van het getekende uitgifteformulier, of null. */
  uitgifteDocumentId?: string | null
  /** De ledgerregel van de uitgifte (voor de foto's van destijds), of null. */
  uitgifteMutatieId?: string | null
  /** Aantal foto's dat bij de uitgifte is vastgelegd. */
  uitgifteFotoAantal?: number
  /** Het middel achter deze regel in het middelen-ledger (voor overdragen aan een collega). */
  middel?: OffboardingRegelMiddel | null
  /** batch-67 (FO-505 §A1) - stuks van deze regel (bulk: netto op naam); leeg = 1. */
  aantal?: number
}

/**
 * Het middel achter een inname-regel, in de termen van het middelen-ledger
 * (`MiddelType` uit `middelen.ts`; hier als letterlijke union om een
 * import-cyclus tussen beide bestanden te vermijden).
 */
export interface OffboardingRegelMiddel {
  type: 'VOORRAAD_UNIEK_ITEM' | 'UITRUSTING_ITEM' | 'VOERTUIG' | 'BULK_ARTIKEL' | 'TANKPAS'
  id: string
}

export type OffboardingAccountStapKey =
  | 'ACCESS'
  | 'ENTRA_BLOKKADE'
  | 'CONTACT_RELEASES'
  | 'ABONNEMENTEN'
  // batch-66 kluis-niveaus (FO-495 §2.A.3) - de vertrekkende medewerker is nog
  // eigenaar van kluis-geheimen: overdragen is verplicht, niet overslaanbaar.
  | 'KLUIS_EIGENAARSCHAP'

export interface OffboardingAccountStapDTO {
  key: OffboardingAccountStapKey
  label: string
  status: 'OPEN' | 'KLAAR' | 'NVT'
  detail: string | null
  /** Deeplink naar de bestaande beheer-plek (Middelen-tab, Entra, ...). */
  href: string | null
}

export type OffboardingEmmerVoorstel = 'UITBETALEN' | 'OPNEMEN'

export interface OffboardingEindafrekeningEmmerDTO {
  emmer: string
  label: string
  /** Beschikbaar saldo op einddatum (pro-rata al geboekt). Decimal-string uren. */
  beschikbaarUren: string
  voorstel: OffboardingEmmerVoorstel
  /** Of deze emmer volgens de instellingen uitbetaalbaar is (wettelijk/bovenwettelijk altijd). */
  uitbetaalbaar: boolean
}

export interface OffboardingVerrekenRegelDTO {
  bronSubKey: string
  omschrijving: string
  /** Decimal-string EUR (overname-restwaarde). */
  bedrag: string
}

export interface OffboardingEindafrekeningDTO {
  emmers: OffboardingEindafrekeningEmmerDTO[]
  verrekenregels: OffboardingVerrekenRegelDTO[]
  bevestigd: boolean
  bevestigdOp: string | null
  /** Gezet bij einddatum-verschuiving ná bevestiging (T12): ter herbeoordeling SALARIS. */
  herbeoordeelReden: string | null
}

export interface OffboardingDocumentStapDTO {
  key: 'GETUIGSCHRIFT' | 'EINDEBRIEF'
  label: string
  /** Of er een MEDEWERKER-scope sjabloon geconfigureerd is om te genereren. */
  templateBeschikbaar: boolean
  /**
   * FO-459 (batch-62) - het sjabloon dat de generatie gebruikt (zelfde selectie);
   * de briefpapier-kiezer leest er de template-standaard en de eigen-
   * briefpapier-vlag mee. Null = geen sjabloon.
   */
  templateId: string | null
  documentId: string | null
  gegenereerd: boolean
  /** FO-385 verstrek: zichtbaar/verstuurd naar de medewerker. */
  verstrekt: boolean
}

export interface OffboardingWgvStapDTO {
  openAanvragen: number
  lopendeVerklaringen: number
}

export interface OffboardingExitFormulierDTO {
  templateId: string | null
  templateNaam: string | null
  ingevuld: boolean
}

export interface OffboardingSamenloopVerlofDTO {
  aanvraagId: string
  startDatum: string
  eindDatum: string
}

export interface OffboardingSamenloopDTO {
  /** Open ziekmelding bij uitdienst (verzuim loopt door). */
  ziekteOpen: boolean
  /** Goedgekeurd verlof ná de einddatum (voorstel tot intrekken). Backoffice-only. */
  verlofNaEinddatum: OffboardingSamenloopVerlofDTO[]
  /** Aantal geplande werkbon-dagen ná de einddatum (ACTIE naar planner). */
  planningNaEinddatum: number
}

export interface OffboardingBlokkadeDTO {
  geblokkeerd: boolean
  /** Primaire "wacht op X"-reden, of null. */
  reden: string | null
  sublijst: string[]
}

export interface OffboardingTaakDTO {
  id: string
  titel: string
  status: string
  stap: string | null
  deadline: string | null
  uitvoerderNaam: string | null
}

// ─── batch-48 offboarding-cockpit (spec 94) ───────────────────────────────────

/**
 * Spec 94 §3.2 — "Afmelden salarisverwerking": een expliciete afvink-stap met
 * NMBRS-aandachtspunten, GEEN NMBRS-API-koppeling (A2). Datum + actor komen uit
 * de bestaande stap-taak (`bronSubKey = SALARISVERWERKING`), dus deze stap kost
 * geen kolom en geen migratie.
 */
export interface OffboardingSalarisStapDTO {
  afgemeld: boolean
  /** ISO-datum van de afmelding (uit de stap-taak). */
  afgemeldOp: string | null
  afgemeldDoorNaam: string | null
  /** ISO-datum-deadline (= einddatum). */
  deadline: string | null
  /**
   * NMBRS-aandachtspunten in gebruikerstaal: laatste verloningsperiode,
   * eindafrekening in de export, ziek uit dienst (§6.4), verlofschuld. Nooit
   * blokkerend - de salarisverwerker beslist.
   */
  aandachtspunten: string[]
  /** batch-71 offboarding-inname (FO-535 §B7) - de afmeldmail aan de salarisadministratie. */
  afmeldMail: OffboardingSalarisAfmeldMailDTO
  /** De poort op de knop "Afmelden bij salarisadministratie" (§B8). Dezelfde regel als de server. */
  poort: OffboardingAfmeldPoortDTO
}

/** batch-71 (FO-535 §B7) - de uitkomst van de afmeldmail. */
export const OFFBOARDING_SALARIS_AFMELDING_STATUSSEN = ['VERSTUURD', 'GEEN_ADRES', 'MISLUKT', 'AANGEHOUDEN'] as const
export type OffboardingSalarisAfmeldingStatus = (typeof OFFBOARDING_SALARIS_AFMELDING_STATUSSEN)[number]

export interface OffboardingSalarisAfmeldMailDTO {
  status: OffboardingSalarisAfmeldingStatus | null
  /** ISO-datetime van de laatst verstuurde afmeldmail. */
  verstuurdOp: string | null
  /** ISO-datetime van de bevestiging door de salarisadministratie. */
  bevestigdOp: string | null
  bevestigingBron: 'MAIL' | 'HANDMATIG' | null
  bevestigingNotitie: string | null
  bevestigdDoorNaam: string | null
  /** ISO-datetime waarop de afmelding is aangehouden (eindafrekening ingetrokken, §6.4). */
  aangehoudenOp: string | null
  /** Afgemeld via de oude afvink-stap, dus zonder mail: de mail kan alsnog. */
  zonderMailAfgemeld: boolean
}

/**
 * De poort op het afmelden (§B8): pas open zodra de eindafrekening naar de
 * salarisadministratie is verstuurd, of de stap "buiten het systeem" is afgehandeld.
 * In dat laatste geval hoort er een bevestigde aanleverdatum bij.
 */
export interface OffboardingAfmeldPoortDTO {
  open: boolean
  /** Waarom de knop uit staat; null als hij open is. */
  reden: string | null
  /** Open via de variant BUITEN_SYSTEEM: de datum "buiten de app aangeleverd" is verplicht. */
  vereistBuitenSysteemDatum: boolean
  /** ISO-datetime waarop de eindafrekening naar de salarisadministratie ging. */
  eindafrekeningVerstuurdNawOp: string | null
}

/** batch-71 (FO-535 §B9) - een mail aan de salarisadministratie, voor de tijdlijn in de cockpit. */
export interface OffboardingSalarisMailDTO {
  id: string
  /** Wat voor mail: eindafrekening, afmelding, aangehouden of intrekking. */
  soort: 'EINDAFREKENING' | 'AFMELDING' | 'AANGEHOUDEN' | 'INTREKKING'
  onderwerp: string
  aan: string[]
  cc: string[]
  /** QUEUED / SENT / FAILED / ... van de uitgaande mail. */
  status: string
  /** ISO-datetime: verzonden, anders in de wachtrij gezet. */
  op: string
}

/** De afmelding die nog op een bevestiging wacht, ook als de offboarding al is afgerond (§B7). */
export interface OffboardingOpenAfmeldingDTO {
  workflowId: string
  /** ISO-datetime. */
  verstuurdOp: string
  onderwerp: string | null
  aan: string[]
}

/** De ontvangers van de NAW-mails, om vooraf te tonen in de bevestigingsdialoog. */
export interface OffboardingSalarisadministratieDTO {
  adresIngesteld: boolean
  naam: string | null
  aan: string[]
  cc: string[]
  /** De instelling waar de adressen staan (GEEN_ADRES linkt hierheen, §6.5). */
  instellingenHref: string
  /** Staat in het onderwerp van de mails; de dialoog toont dat vooraf. */
  personeelsnummer: string | null
  /**
   * De afmeldmail staat aan (Payroll, Loonstroken, Instellingen). Start uit tot de PO
   * akkoord geeft: dan vinkt de knop de afmelding af zonder mail.
   */
  afmeldmailActief: boolean
}

/** Status van een afmelding die is afgevinkt zonder mail (de afmeldmail staat uit). */
export const SALARIS_AFMELDING_ZONDER_MAIL = 'ZONDER_MAIL' as const

/** Pad van de instelling met de adressen van de salarisadministratie (FO-417). */
export const OFFBOARDING_SALARIS_INSTELLINGEN_HREF = '/payroll/loonstroken/instellingen'

/** Spiegelt `HrGesprekStatus`; VERZET hoort er expliciet bij (anders leest een
 *  verzet gesprek als "gepland" en klopt de cockpit niet met de agenda). */
export type OffboardingExitgesprekStatus = 'GEPLAND' | 'VERZET' | 'AFGEROND' | 'GEANNULEERD'

/**
 * Spec 94 §3.2 — exitgesprek via de BESTAANDE `HrGesprek`-aanmaak (gesprekstype
 * exitgesprek) met bronverwijzing naar de offboarding in de workflow-audit.
 * `beschikbaar: false` = het gesprekstype is (nog) niet geconfigureerd; de
 * cockpit degradeert dan naar een uitleg + link naar de gespreksagenda.
 */
export interface OffboardingExitgesprekDTO {
  /** Id van het GesprekTypeConfig-record met key `EXITGESPREK`, of null. */
  gesprekTypeConfigId: string | null
  /** Standaardduur uit de gesprekstype-config (minuten), voor de plan-actie. */
  duurMinuten: number
  beschikbaar: boolean
  gesprekId: string | null
  /** ISO-datetime van het geplande gesprek. */
  geplandOp: string | null
  status: OffboardingExitgesprekStatus | null
  /** Status van het gekoppelde gespreksverslag (FO-387-cyclus), als dat er is. */
  verslagStatus: string | null
}

export const OFFBOARDING_VERLOFSCHULD_KEUZES = ['VERREKENEN', 'KWIJTSCHELDEN'] as const
export type OffboardingVerlofschuldKeuze = (typeof OFFBOARDING_VERLOFSCHULD_KEUZES)[number]

export const OFFBOARDING_VERLOFSCHULD_KEUZE_LABELS: Record<OffboardingVerlofschuldKeuze, string> = {
  VERREKENEN: 'Verrekenen met de eindafrekening',
  KWIJTSCHELDEN: 'Kwijtschelden (niet verrekenen)',
}

/**
 * Spec 94 §6.5 (+ spec 39) — te veel opgenomen verlof bij uitdiensttreding. De
 * keuze verrekenen/kwijtschelden is EXPLICIET: nooit stil verrekenen en nooit
 * stil laten lopen. `keuzeVereist` = er staat een schuld open en de
 * eindafrekening kan niet bevestigd worden zonder keuze.
 */
export interface OffboardingVerlofschuldRegelDTO {
  emmer: string
  label: string
  /** Positief getal: het aantal uren dat te veel is opgenomen (Decimal-string). */
  tekortUren: string
}

export interface OffboardingVerlofschuldDTO {
  regels: OffboardingVerlofschuldRegelDTO[]
  /** Totaal te veel opgenomen uren (Decimal-string, positief). */
  totaalUren: string
  keuzeVereist: boolean
  /** Vastgelegde keuze (na bevestiging van de eindafrekening), of null. */
  keuze: OffboardingVerlofschuldKeuze | null
}

/** Eén rij op de `/offboarding`-lijst (lopende + geplande offboardings). */
export interface OffboardingLijstRowDTO {
  workflowId: string
  employeeId: string
  medewerkerNaam: string
  functie: string | null
  bvNaam: string | null
  /** ISO-datum (yyyy-mm-dd). */
  einddatum: string
  /** Negatief = de einddatum is verstreken. */
  dagenTotEinddatum: number
  /** true zolang de einddatum in de toekomst ligt (gepland uit dienst). */
  gepland: boolean
  status: OffboardingWorkflowStatus
  stappenKlaar: number
  stappenTotaal: number
  openStappen: string[]
  geblokkeerd: boolean
  /** §6.4 - open ziekmelding bij uitdiensttreding (aandachtspunt salarisverwerking). */
  ziekteOpen: boolean
  eindafrekeningBevestigd: boolean
  salarisAfgemeld: boolean
}

export interface OffboardingLijstStatsDTO {
  /** Lopende workflows (alle). */
  lopend: number
  /** Waarvan de einddatum nog in de toekomst ligt. */
  gepland: number
  /** Waarvan de einddatum verstreken is (ongeacht of er nog stappen open staan). */
  teLaat: number
}

export interface OffboardingCockpitDTO {
  workflowId: string
  employeeId: string
  medewerkerNaam: string
  functie: string | null
  /** ISO-datum (yyyy-mm-dd) van de einddatum. */
  einddatum: string | null
  status: OffboardingWorkflowStatus
  gestartOp: string | null
  geforceerdAfgerond: boolean
  forceerMotivatie: string | null
  /** Voortgang: afgeronde verplichte stappen / totaal. */
  stappenKlaar: number
  stappenTotaal: number
  /**
   * batch-48 (spec 94 §3.1) — de einddatum ligt vandaag of later: de medewerker
   * is nog gewoon in dienst en blijft overal zichtbaar ("Uit dienst per ...").
   */
  geplandUitDienst: boolean
  /** Dagen tot de einddatum (negatief = verstreken). `null` zonder einddatum. */
  dagenTotEinddatum: number | null
  inname: OffboardingInnameRegelDTO[]
  accounts: OffboardingAccountStapDTO[]
  eindafrekening: OffboardingEindafrekeningDTO
  /** batch-48 (spec 94 §6.5 / spec 39) — te veel opgenomen verlof, expliciete keuze. */
  verlofschuld: OffboardingVerlofschuldDTO
  /**
   * batch-58 uitdienst-verlof (FO-424 §3.3) — de verlof-stap: wat de
   * uitdienst-transactie met verlof ná de einddatum heeft gedaan (tellingen uit de
   * workflow-audit), wat er nog handmatig moet (werklijst) en welke correcties door
   * een latere einddatum onnodig zijn geworden.
   */
  verlofStap: OffboardingVerlofStapDTO
  /** batch-48 (spec 94 §3.2) — afmelden bij de salarisverwerking (afvink-stap). */
  salarisverwerking: OffboardingSalarisStapDTO
  /** batch-48 (spec 94 §3.2) — exitgesprek via de bestaande HrGesprek-flow. */
  exitgesprek: OffboardingExitgesprekDTO
  documenten: OffboardingDocumentStapDTO[]
  /**
   * batch-57 overeenkomst-vervolg (FO-415 §6.4) — openstaande bruikleen-taken van
   * deze medewerker: middelen die hij in gebruik heeft zonder (getekende)
   * bruikleenovereenkomst. Ze mogen niet met hem de deur uit; daarom schuiven ze
   * de offboarding-cockpit in i.p.v. stil in de takenlijst achter te blijven.
   */
  openBruikleen: BruikleenTriggerDTO[]
  wgv: OffboardingWgvStapDTO
  /** ISO-datum van de AVG-opschoon-streefdatum (einddatum + bewaartermijn). */
  avgOpschoonStreefdatum: string | null
  exitFormulier: OffboardingExitFormulierDTO
  samenloop: OffboardingSamenloopDTO
  blokkade: OffboardingBlokkadeDTO
  taken: OffboardingTaakDTO[]
  /** Toegang tot toekomstige stappen (mirror van server-permissies voor UI-hints). */
  graphBlokkadeOptOut: boolean
  graphBlokkadeBeschikbaar: boolean
  graphGeblokkeerd: boolean
  accessIngetrokken: boolean
  // ── batch-61 offboarding-afronding (FO-451 + FO-452) ─────────────────────
  /** De persistente inleverlijst (uit bezit + handmatig; verwijderd blijft doorgehaald). */
  inleverlijst: OffboardingInleverItemDTO[]
  inleverbrief: OffboardingInleverbriefDTO
  /** Innamesessies + verstuurde bevestigingen (de live inname-regels staan in `inname`). */
  innameSessies: OffboardingInnameDTO
  eindafrekeningDocument: OffboardingEindafrekeningDocumentDTO
  /** Gemotiveerd overgeslagen stappen ("overgeslagen: {reden}"). */
  overgeslagenStappen: OffboardingStapOverslagDTO[]
  medewerkerContact: OffboardingMedewerkerContactDTO
  // ── batch-71 offboarding-inname (FO-535) ─────────────────────────────────
  /** De ontvangers van de NAW-mails (§B6, §B7). */
  salarisadministratie: OffboardingSalarisadministratieDTO
  /** Alle mails aan de salarisadministratie, nieuwste eerst (§B9). */
  salarisMails: OffboardingSalarisMailDTO[]
  /** De FO-516-innamelijsten van deze medewerker, voor de doorklik en de voortgang (§C13). */
  innameLijsten: OffboardingInnameLijstDTO[]
  /** Regels van de inleverlijst waar cockpit en innamelijst het niet over eens zijn (§6.6). */
  innameConflicten: number
}

/** batch-71 (FO-535 §C13) - een FO-516-innamelijst (`InnameSessie`) in de cockpit. */
export interface OffboardingInnameLijstDTO {
  id: string
  nummer: string
  status: 'OPEN' | 'AFGESLOTEN'
  afgevinkt: number
  totaal: number
  /** ISO-datetime. */
  geopendOp: string
}

// batch-67 middelen-inname: de staat-vocabulaire staat vóór de actie-schema's (TDZ-vrij).
export const OFFBOARDING_INLEVER_STATEN = ['GOED', 'GEBRUIKSSPOREN', 'BESCHADIGD', 'ONVOLLEDIG'] as const
export type OffboardingInleverStaat = (typeof OFFBOARDING_INLEVER_STATEN)[number]
export const OFFBOARDING_INLEVER_STAAT_LABELS: Record<OffboardingInleverStaat, string> = {
  GOED: 'Goed',
  GEBRUIKSSPOREN: 'Gebruikssporen',
  BESCHADIGD: 'Beschadigd',
  ONVOLLEDIG: 'Onvolledig',
}

// ─── Actie-schemas (gedeeld: API `.parse` + client-side validatie) ─────────────

export const afhandelInnameRegelSchema = z
  .object({
    modus: z.enum(OFFBOARDING_INNAME_MODUS_AFHANDELEN),
    /** Doellocatie bij INNEMEN van een uniek/retourplicht-item (verplicht dan). */
    locatieId: idParam('locatieId').optional().nullable(),
    conditie: z.string().trim().max(500).optional().nullable(),
    /** batch-67 middelen-inname (FO-505 §12.2) - staat bij INNEMEN; BESCHADIGD zet een uniek item op DEFECT. */
    staat: z.enum(OFFBOARDING_INLEVER_STATEN).optional().nullable(),
    /** Restwaarde EUR bij OVERNEMEN (>= 0; 0 = schenking, apart geaudit). */
    restwaarde: z.number().min(0).max(1_000_000).optional().nullable(),
    reden: z.string().trim().max(2000).optional().nullable(),
    /**
     * batch-67 (FO-505 §C10) - is de eindafrekening al definitief, dan vraagt een
     * overname (een nieuw verrekenbedrag) de reden van de nieuwe versie.
     */
    nieuweVersieReden: z.string().trim().max(2000).optional().nullable(),
  })
  .superRefine((val, ctx) => {
    if (val.modus === 'OVERNEMEN') {
      if (val.restwaarde === undefined || val.restwaarde === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['restwaarde'],
          message: 'Restwaarde is verplicht bij overnemen (0 toegestaan).',
        })
      }
      if (!val.reden || val.reden.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['reden'],
          message: 'Reden is verplicht bij overnemen.',
        })
      }
    }
    if (val.modus === 'AFBOEKEN' && (!val.reden || val.reden.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reden'],
        message: 'Reden is verplicht bij afboeken.',
      })
    }
  })
export type AfhandelInnameRegelInput = z.infer<typeof afhandelInnameRegelSchema>

export const bevestigEindafrekeningSchema = z.object({
  voorstellen: z
    .array(
      z.object({
        emmer: z.string().min(1),
        voorstel: z.enum(['UITBETALEN', 'OPNEMEN']),
      }),
    )
    .default([]),
  /**
   * batch-48 (spec 94 §6.5 / spec 39) — verplicht ZODRA er een verlofschuld
   * staat. Optioneel in het schema (de server weet pas na het herrekenen óf er
   * een schuld is en geeft dan een nette 422 met deze veldnaam), nooit een
   * stille default: verrekenen met iemands laatste loonstrook zonder expliciete
   * keuze is precies wat spec 39 verbiedt.
   */
  verlofschuldKeuze: z.enum(OFFBOARDING_VERLOFSCHULD_KEUZES).optional(),
})
export type BevestigEindafrekeningInput = z.infer<typeof bevestigEindafrekeningSchema>

/** batch-48 (spec 94 §3.2) — koppel een bestaand HR-gesprek als exitgesprek. */
export const offboardingKoppelExitgesprekSchema = z.object({
  gesprekId: idParam('gesprekId'),
})
export type OffboardingKoppelExitgesprekInput = z.infer<
  typeof offboardingKoppelExitgesprekSchema
>

/** batch-48 (spec 94 §3.2) — query van de `/offboarding`-lijst. */
export const offboardingLijstQuerySchema = z.object({
  zoek: z.string().trim().max(200).optional(),
  /** Alleen geplande (einddatum in de toekomst) of alleen verstreken offboardings. */
  fase: z.enum(['ALLE', 'GEPLAND', 'TE_LAAT']).default('ALLE'),
  status: z.enum(OFFBOARDING_WORKFLOW_STATUS).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
})
export type OffboardingLijstQuery = z.infer<typeof offboardingLijstQuerySchema>

/** batch-48 (spec 94 §7) — formaat van de eindafrekening-export. */
export const OFFBOARDING_EXPORT_FORMATEN = ['pdf', 'excel'] as const
export type OffboardingExportFormaat = (typeof OFFBOARDING_EXPORT_FORMATEN)[number]

/** Afronden (spiegel FO-381): motivatie verplicht wanneer geforceerd. */
export const OFFBOARDING_GEFORCEERD_LABEL = 'Geforceerd afronden (motivatie verplicht)'
export const afrondOffboardingSchema = z
  .object({
    geforceerd: z.boolean().default(false),
    motivatie: z.string().trim().max(2000).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.geforceerd && (!val.motivatie || val.motivatie.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['motivatie'],
        message: 'Motivatie is verplicht bij geforceerd afronden.',
      })
    }
  })
export type AfrondOffboardingInput = z.infer<typeof afrondOffboardingSchema>

export const offboardingGraphOptOutSchema = z.object({ optOut: z.boolean() })
export type OffboardingGraphOptOutInput = z.infer<typeof offboardingGraphOptOutSchema>

export const offboardingKoppelExitFormulierSchema = z.object({
  templateId: idParam('templateId').nullable(),
})
export type OffboardingKoppelExitFormulierInput = z.infer<
  typeof offboardingKoppelExitFormulierSchema
>

export const offboardingGenereerDocumentSchema = z.object({
  soort: z.enum(['GETUIGSCHRIFT', 'EINDEBRIEF']),
  /** FO-459 (batch-62) - briefpapierkeuze uit de kiezer; leeg = cascade. */
  briefpapier: BriefpapierKeuzeInvoerSchema.nullable().optional(),
})
export type OffboardingGenereerDocumentInput = z.infer<typeof offboardingGenereerDocumentSchema>

export const offboardingVerstrekDocumentSchema = z.object({
  documentId: idParam('documentId'),
})
export type OffboardingVerstrekDocumentInput = z.infer<typeof offboardingVerstrekDocumentSchema>

// ─── batch-61 offboarding-afronding (FO-451 + FO-452) ─────────────────────────
//
// Inleverlijst (persistent, met verwijderen-met-reden), formele inleverbrief,
// inname-vinklijst + bevestiging, eindafrekening-document met vier-ogen-akkoord
// en stap-overslaan-met-reden. Spiegelt de Prisma-enums in de sectie
// `// batch-61 offboarding-afronding` van het schema.

export const OFFBOARDING_INLEVER_STATUS = ['OPEN', 'INGELEVERD', 'NIET_INGELEVERD', 'VERWIJDERD'] as const
export type OffboardingInleverStatus = (typeof OFFBOARDING_INLEVER_STATUS)[number]
export const OFFBOARDING_INLEVER_STATUS_LABELS: Record<OffboardingInleverStatus, string> = {
  OPEN: 'In te leveren',
  INGELEVERD: 'Ingeleverd',
  NIET_INGELEVERD: 'Niet ingeleverd',
  VERWIJDERD: 'Verwijderd',
}


export const OFFBOARDING_VERVOLGACTIES = ['NASTUREN', 'KOSTEN_VERREKENEN', 'AFGEBOEKT'] as const
export type OffboardingVervolgactie = (typeof OFFBOARDING_VERVOLGACTIES)[number]
export const OFFBOARDING_VERVOLGACTIE_LABELS: Record<OffboardingVervolgactie, string> = {
  NASTUREN: 'Nasturen (later alsnog inleveren)',
  KOSTEN_VERREKENEN: 'Kosten verrekenen in de eindafrekening',
  AFGEBOEKT: 'Afgeboekt (geen verrekening)',
}

export const OFFBOARDING_DOCUMENT_SOORTEN = ['INLEVERBRIEF', 'INNAME_BEVESTIGING', 'EINDAFREKENING'] as const
export type OffboardingDocumentSoort = (typeof OFFBOARDING_DOCUMENT_SOORTEN)[number]
export const OFFBOARDING_DOCUMENT_SOORT_LABELS: Record<OffboardingDocumentSoort, string> = {
  INLEVERBRIEF: 'Inleverbrief',
  INNAME_BEVESTIGING: 'Inname-bevestiging',
  EINDAFREKENING: 'Eindafrekening',
}

export const OFFBOARDING_DOCUMENT_STATUSSEN = ['CONCEPT', 'DEFINITIEF', 'VERVALLEN'] as const
export type OffboardingDocumentStatus = (typeof OFFBOARDING_DOCUMENT_STATUSSEN)[number]
export const OFFBOARDING_DOCUMENT_STATUS_LABELS: Record<OffboardingDocumentStatus, string> = {
  CONCEPT: 'Concept (wacht op akkoord)',
  DEFINITIEF: 'Definitief',
  VERVALLEN: 'Vervallen',
}

export const OFFBOARDING_AKKOORD_ROLLEN = ['SALARIS', 'HR'] as const
export type OffboardingAkkoordRol = (typeof OFFBOARDING_AKKOORD_ROLLEN)[number]
export const OFFBOARDING_AKKOORD_ROL_LABELS: Record<OffboardingAkkoordRol, string> = {
  SALARIS: 'Akkoord salarisadministratie',
  HR: 'Akkoord HR',
}

export const OFFBOARDING_VERZENDKANALEN = ['MAIL', 'MAIL_PORTAL', 'PORTAL', 'POST'] as const
export type OffboardingVerzendkanaal = (typeof OFFBOARDING_VERZENDKANALEN)[number]
export const OFFBOARDING_VERZENDKANAAL_LABELS: Record<OffboardingVerzendkanaal, string> = {
  MAIL: 'per e-mail',
  MAIL_PORTAL: 'per e-mail en in de portal',
  PORTAL: 'in de portal',
  POST: 'per post (geen e-mailadres bekend)',
}

/** Uitkomst van de laatste verzending aan de medewerker (zichtbaar in de cockpit). */
export const OFFBOARDING_VERZEND_STATUSSEN = ['VERSTUURD', 'MAIL_MISLUKT', 'PORTAL', 'POST'] as const
export type OffboardingVerzendStatus = (typeof OFFBOARDING_VERZEND_STATUSSEN)[number]
export const OFFBOARDING_VERZEND_STATUS_LABELS: Record<OffboardingVerzendStatus, string> = {
  VERSTUURD: 'Verstuurd',
  MAIL_MISLUKT: 'Mail niet verstuurd',
  PORTAL: 'Alleen in de portal (geen e-mailadres)',
  POST: 'Per post (geen e-mailadres, geen portal)',
}

/** Overslag-variant van de eindafrekening-stap (FO-452 §2.3). */
export const OFFBOARDING_OVERSLAG_VARIANT_BUITEN_SYSTEEM = 'BUITEN_SYSTEEM'
export const OFFBOARDING_OVERSLAG_VARIANTEN = [OFFBOARDING_OVERSLAG_VARIANT_BUITEN_SYSTEEM] as const
export type OffboardingOverslagVariant = (typeof OFFBOARDING_OVERSLAG_VARIANTEN)[number]

/**
 * Vaste (niet-inname) cockpit-stappen die overgeslagen kunnen worden, met hun
 * gebruikerslabel. Inname-regels ("INNAME:<soort>:<id>") en handmatige
 * inlever-items ("HANDMATIG:<id>") zijn eveneens overslaanbaar; hun label is de
 * omschrijving van het item.
 */
/**
 * `OperationeleTaak.bronSubKey` van de ene taak per offboarding ("Offboarding
 * <naam>", bronModule 'offboarding'). De takenlijst opent daarmee direct de
 * offboarding-cockpit met alle actiepunten.
 */
export const OFFBOARDING_HOOFDTAAK_SUBKEY = 'OFFBOARDING'

export const OFFBOARDING_STAP_LABELS: Record<string, string> = {
  ACCESS: 'Toegang intrekken',
  ABONNEMENTEN: 'Abonnementen opzeggen of omhangen',
  CONTACT_RELEASES: 'Klant-contactvrijgaven intrekken',
  EINDAFREKENING: 'Verlof-eindafrekening bevestigen',
  SALARISVERWERKING: 'Afmelden salarisverwerking',
  AVG_OPSCHONING: 'Dossier opschonen (AVG)',
  EXITGESPREK: 'Exitgesprek',
  INLEVERBRIEF: 'Inleverbrief versturen',
  INNAME_BEVESTIGING: 'Inname-bevestiging versturen',
  EINDAFREKENING_DOCUMENT: 'Eindafrekening-document',
}

/** Gebruikerstaal voor een overslag: "overgeslagen: {reden}" (+ variant). */
export function offboardingOverslagLabel(overslag: {
  reden: string
  variant?: string | null
}): string {
  if (overslag.variant === OFFBOARDING_OVERSLAG_VARIANT_BUITEN_SYSTEEM) {
    return `buiten het systeem afgehandeld: ${overslag.reden}`
  }
  return `overgeslagen: ${overslag.reden}`
}

export interface OffboardingStapOverslagDTO {
  id: string
  stapKey: string
  label: string
  reden: string
  variant: OffboardingOverslagVariant | null
  doorNaam: string | null
  /** ISO-datetime. */
  op: string
}

/** Eén regel op de persistente inleverlijst (backoffice; nooit naar de portal met bedragen). */
export interface OffboardingInleverItemDTO {
  id: string
  bronSubKey: string
  soort: OffboardingItemSoort | 'HANDMATIG'
  omschrijving: string
  /** Kenteken / IMEI / serienummer / assetTag. */
  kenmerk: string | null
  detail: string | null
  handmatig: boolean
  status: OffboardingInleverStatus
  staat: OffboardingInleverStaat | null
  staatToelichting: string | null
  fotoAantal: number
  ontvangenDoorNaam: string | null
  /** ISO-datetime. */
  ontvangenOp: string | null
  innameSessieId: string | null
  vervolgactie: OffboardingVervolgactie | null
  vervolgactieReden: string | null
  /** Decimal-string EUR (alleen bij KOSTEN_VERREKENEN). */
  vervolgactieBedrag: string | null
  /** ISO-datum (yyyy-mm-dd). */
  vervolgactieTermijn: string | null
  /** ISO-datetime van de verwijdering (regel blijft doorgehaald zichtbaar). */
  verwijderdOp: string | null
  verwijderReden: string | null
  verwijderdDoorNaam: string | null
  /** true zodra de regel niets meer vraagt (ingeleverd, afgeboekt/verrekend, verwijderd of overgeslagen). */
  afgehandeld: boolean
  /** Live bron-item nog in bezit: innemen via het bezit-pad is mogelijk. */
  kanInnemen: boolean
  kanAfboeken: boolean
  vereistLocatie: boolean
  vereistInhoudscontrole: boolean
  inhoudscontroleGereed: boolean
  overgeslagen: OffboardingStapOverslagDTO | null
  // batch-67 middelen-inname (FO-499 §C8 / FO-505 §A1) — alleen gevuld zolang het
  // item nog bij de medewerker ligt (live regel).
  /** Het middel in het middelen-ledger; null bij een handmatige regel of een al afgehandeld item. */
  middel?: OffboardingRegelMiddel | null
  /** ISO-datetime van de uitgifte volgens het ledger, of null (bezit van vóór het ledger). */
  uitgegevenOp?: string | null
  /** Document-id van het getekende uitgifteformulier, of null. */
  uitgifteDocumentId?: string | null
  /** De ledgerregel van de uitgifte (voor de foto's van destijds), of null. */
  uitgifteMutatieId?: string | null
  uitgifteFotoAantal?: number
  // batch-67 middelen-inname, fase B (FO-505).
  /** Aantal stuks van deze regel (bulk: het open aantal; verder 1). */
  aantal?: number
  /**
   * Waardevoorstel voor een open of niet-ingeleverd item (§A3). Backoffice-only:
   * de toelichting noemt de prijs waar het voorstel op rust.
   */
  waardeVoorstel?: OffboardingWaardeVoorstelDTO | null
  /** De berekeningstoelichting waarmee de verrekening is vastgelegd. */
  waardeToelichting?: string | null
  /** Wie de vervolgactie koos (de actor van de verrekenregel). */
  vervolgactieDoorNaam?: string | null
  /** Kleding, PBM en niet-geregistreerd bezit: bij een goede staat hoort de keuze terug op voorraad of afvoeren. */
  vraagtVoorraadActie?: boolean
  /** Het catalogusartikel van dit item; null = onbekend (kies het bij "terug op voorraad"). */
  voorraadArtikel?: { id: string; naam: string } | null
  /**
   * Kleding of PBM zonder catalogusartikel (de juli-import legde alleen een
   * omschrijving vast): die omschrijving, ter herkenning bij het kiezen van het
   * artikel. Het artikel wordt er nooit uit geraden.
   */
  registratieOmschrijving?: string | null
  voorraadActie?: OffboardingVoorraadActie | null
  /** Het reparatieverzoek dat uit deze regel is ontstaan. */
  reparatieVerzoek?: { id: string; status: ReparatieVerzoekStatus } | null
  /** Beschadigd of onvolledig ingeleverd, nog zonder reparatieverzoek of verrekening. */
  reparatieVoorgesteld?: boolean
  // batch-71 offboarding-inname (FO-535 §A5, §C13).
  afhandelingBron?: OffboardingAfhandelingBron | null
  /** Waar de regel staat, in gewone taal: "ingenomen via innamelijst INN-1001 door Thomas op 12-11-2026". */
  afhandelingLabel?: string
  /** De FO-516-innamelijst waarop dit item staat: de knop "open in innamelijst". */
  innameLijst?: { id: string; nummer: string } | null
  /** Open conflict tussen cockpit en innamelijst (§6.6): HR beslist. */
  innameConflict?: { op: string; toelichting: string | null } | null
}

/** Terug op voorraad of afvoeren (FO-505 §12.5 / §12.6). */
export const OFFBOARDING_VOORRAAD_ACTIES = ['TERUG_OP_VOORRAAD', 'AFVOEREN'] as const
export type OffboardingVoorraadActie = (typeof OFFBOARDING_VOORRAAD_ACTIES)[number]
export const OFFBOARDING_VOORRAAD_ACTIE_LABELS: Record<OffboardingVoorraadActie, string> = {
  TERUG_OP_VOORRAAD: 'Terug op voorraad',
  AFVOEREN: 'Afvoeren (niet meer uit te geven)',
}

/** Het waardevoorstel van `stelWaardeVoor` zoals de backoffice het toont. */
export interface OffboardingWaardeVoorstelDTO {
  /** Decimal-string EUR voor alle stuks samen; null = geen voorstel (handmatig, met reden). */
  bedrag: string | null
  bron: 'AANSCHAFWAARDE' | 'CATALOGUSPRIJS' | 'LAATSTE_INKOOP' | null
  toelichting: string
  leegReden: 'GEEN_PRIJS' | 'GEEN_DATUM' | null
}

/** Verschil-overzicht bovenaan de inname (FO-505 §A2), geteld in stuks. */
export interface OffboardingInleverVerschilDTO {
  uitgegeven: number
  ingeleverd: number
  /** Niet ingeleverd plus nog niet beoordeeld. */
  ontbreekt: number
  beschadigd: number
  /** Nog niet beoordeeld: blokkeert het afronden van de inname. */
  open: number
  /** Niet ingeleverd met "nasturen": wordt nog verwacht en blijft dus open. */
  nasturen: number
  /** Niets meer open: elk item is ingeleverd, afgehandeld of gemotiveerd overgeslagen. */
  compleet: boolean
}

export interface OffboardingInnameSessieDTO {
  id: string
  /** ISO-datetime. */
  datum: string
  ontvangenDoorNaam: string | null
  plek: string | null
  opmerking: string | null
  aantalIngeleverd: number
  aantalNietIngeleverd: number
  /** ISO-datetime van de verstuurde bevestiging, of null. */
  bevestigingVerstuurdOp: string | null
  bevestigingVersieId: string | null
  /** batch-67 (FO-499 §C8) — het getekende innameformulier van deze sessie (Document.id), of null. */
  formulierDocumentId?: string | null
  /** De medewerker heeft bij deze inname getekend voor wat hij inleverde. */
  getekendDoorMedewerker?: boolean
}

export interface OffboardingAkkoordDTO {
  rol: OffboardingAkkoordRol
  doorNaam: string | null
  /** ISO-datetime. */
  op: string
  toelichting: string | null
}

export interface OffboardingDocumentVersieDTO {
  id: string
  soort: OffboardingDocumentSoort
  versieNr: number
  status: OffboardingDocumentStatus
  /** Bouwstenen voor de same-origin download-proxy (FO-372): geen kant-en-klare URL. */
  documentId: string
  documentVersieNr: number
  /** ISO-datetime. */
  gegenereerdOp: string
  gegenereerdDoorNaam: string | null
  verstuurdOp: string | null
  verstuurdKanaal: OffboardingVerzendkanaal | null
  verstuurdNaarEmail: string | null
  perPost: boolean
  verzendTeller: number
  /** Uitkomst van de laatste verzending; null zolang niet verstuurd. */
  verzendStatus: OffboardingVerzendStatus | null
  definitiefOp: string | null
  akkoorden: OffboardingAkkoordDTO[]
  salarisverwerkerVerstuurdOp: string | null
  salarisverwerkerStatus: string | null
  innameSessieId: string | null
  /** Voor de portal-zichtbaarheid (FO-385): het Document is vrijgegeven aan de medewerker. */
  portalZichtbaar: boolean
}

/** De inleverafspraak + brieftekst die in de laatste brief staat (prefill voor de volgende versie). */
export interface OffboardingInleverAfspraakDTO {
  /** ISO-datum (yyyy-mm-dd). */
  inleverDatum: string | null
  inleverPlek: string | null
  inleverPersoon: string | null
  contactNaam: string | null
  contactEmail: string | null
  contactTelefoon: string | null
  gevolgenTekst: string
}

export interface OffboardingInleverbriefDTO {
  versies: OffboardingDocumentVersieDTO[]
  laatste: OffboardingDocumentVersieDTO | null
  /** De inleverlijst is gewijzigd sinds de laatste brief: een nieuwe versie is nodig. */
  verouderd: boolean
  afspraak: OffboardingInleverAfspraakDTO | null
  standaardGevolgenTekst: string
}

export interface OffboardingInnameDTO {
  sessies: OffboardingInnameSessieDTO[]
  bevestigingen: OffboardingDocumentVersieDTO[]
  /** Nog niet afgehandelde inlever-items. */
  openItems: number
  /** batch-67 (FO-505 §A2) - uitgegeven, ingeleverd, ontbreekt en beschadigd in stuks. */
  verschil?: OffboardingInleverVerschilDTO
  /**
   * Verbruiksmateriaal en kantoorartikelen die op naam staan maar niet worden
   * teruggevraagd (aantal artikelen); zichtbaar gemaakt zodat er niets stil wegvalt.
   */
  nietTeruggevraagd?: number
}

export interface OffboardingEindafrekeningDocumentDTO {
  versies: OffboardingDocumentVersieDTO[]
  laatste: OffboardingDocumentVersieDTO | null
  /** De opstelling is gewijzigd sinds de laatste versie: nieuwe versie + opnieuw akkoord nodig. */
  verouderd: boolean
  /** De salarisverwerker-adressen (FO-417-instelling) zijn ingesteld; anders kan de opdracht niet de deur uit. */
  salarisverwerkerAdresIngesteld: boolean
  /** batch-67 (FO-505 §C11) - de medewerker heeft bezwaar gemaakt dat nog niet is afgehandeld. */
  onderBezwaar?: boolean
}

export interface OffboardingMedewerkerContactDTO {
  /** Het adres waarop de medewerker berichten ontvangt (prive-mail gaat voor); null = per post. */
  email: string | null
  heeftPortal: boolean
}

// ─── Actie-schemas (gedeeld: API `.safeParse` + client-side validatie) ────────

export const OFFBOARDING_INLEVER_SOORTEN = [
  'VOERTUIG',
  'TANKPAS',
  'UNIEK_ITEM',
  'UITRUSTING',
  'RETOURPLICHT',
  'ABONNEMENT',
  'BULK',
  'HANDMATIG',
] as const

export const inleverItemToevoegenSchema = z
  .object({
    /** Leeg mag alleen met een catalogusartikel: de server neemt dan de artikelnaam. */
    omschrijving: z.string().trim().max(200),
    kenmerk: z.string().trim().max(120).optional().nullable(),
    detail: z.string().trim().max(300).optional().nullable(),
    soort: z.enum(OFFBOARDING_INLEVER_SOORTEN).default('HANDMATIG'),
    /** Keuze uit de middelen-catalogus (VoorraadArtikel-id); de omschrijving wordt dan uit de artikelnaam gevuld. */
    catalogusArtikelId: idParam('catalogusArtikelId').optional().nullable(),
    /** batch-67 (FO-505 §12.6) - aantal stuks van niet-geregistreerd bezit (default 1). */
    aantal: z.number().int().min(1, 'Het aantal is minimaal 1.').max(9999).optional(),
  })
  .superRefine((val, ctx) => {
    if (!val.catalogusArtikelId && val.omschrijving.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['omschrijving'],
        message: 'Omschrijving is verplicht (minimaal 2 tekens), of kies het artikel uit de catalogus.',
      })
    }
  })
export type InleverItemToevoegenInput = z.infer<typeof inleverItemToevoegenSchema>

export const inleverItemVerwijderenSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(3, 'Reden is verplicht bij verwijderen (minimaal 3 tekens).')
    .max(2000),
})
export type InleverItemVerwijderenInput = z.infer<typeof inleverItemVerwijderenSchema>

export const inleverItemHerstellenSchema = z.object({
  reden: z.string().trim().min(3, 'Reden is verplicht bij herstellen.').max(2000),
})
export type InleverItemHerstellenInput = z.infer<typeof inleverItemHerstellenSchema>

const isoDag = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet yyyy-mm-dd zijn')

export const innameItemSchema = z
  .object({
    itemId: idParam('itemId'),
    ingeleverd: z.boolean(),
    staat: z.enum(OFFBOARDING_INLEVER_STATEN).optional().nullable(),
    staatToelichting: z.string().trim().max(2000).optional().nullable(),
    /** Doellocatie bij het innemen van een uniek item (verplicht wanneer `vereistLocatie`). */
    locatieId: idParam('locatieId').optional().nullable(),
    vervolgactie: z.enum(OFFBOARDING_VERVOLGACTIES).optional().nullable(),
    vervolgactieReden: z.string().trim().max(2000).optional().nullable(),
    /** EUR, verplicht (> 0) bij KOSTEN_VERREKENEN. */
    vervolgactieBedrag: z.number().min(0).max(1_000_000, 'Het bedrag is te hoog.').optional().nullable(),
    vervolgactieTermijn: isoDag.optional().nullable(),
    /**
     * batch-67 (FO-499 §A4) — vooraf geuploade bewijsfoto's (client-UUID via
     * `POST /middelen/fotos`). Bij een staat anders dan GOED is minimaal één foto
     * verplicht; de server telt daarbij de foto's mee die al op het item staan.
     */
    fotoIds: z.array(z.string().uuid('Ongeldige foto-verwijzing')).max(10, "Maximaal 10 foto's per item.").optional(),
    // batch-67 middelen-inname, fase B (FO-505).
    /** Bulk-regel: hoeveel stuks er nu worden ingeleverd of afgehandeld (default: alles wat open staat). */
    aantal: z.number().int().min(1, 'Het aantal is minimaal 1.').max(100000).optional(),
    /** Kleding, PBM en niet-geregistreerd bezit in goede staat: terug op voorraad of afvoeren (§12.5 / §12.6). */
    voorraadActie: z.enum(OFFBOARDING_VOORRAAD_ACTIES).optional().nullable(),
    /** Het catalogusartikel, als het item dat zelf niet kent (juli-importregels). */
    voorraadArtikelId: idParam('voorraadArtikelId').optional().nullable(),
  })
  .superRefine((val, ctx) => {
    if (val.ingeleverd) {
      if (!val.staat) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['staat'],
          message: 'Geef de staat op waarin het item is ingeleverd.',
        })
      }
      const nietGoed = val.staat === 'BESCHADIGD' || val.staat === 'ONVOLLEDIG'
      // Een ingeleverd item kent maar één vervolgactie: de schade verrekenen (§A4).
      if (val.vervolgactie && (val.vervolgactie !== 'KOSTEN_VERREKENEN' || !nietGoed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['vervolgactie'],
          message: 'Bij een ingeleverd item kan alleen schade aan een beschadigd of onvolledig item worden verrekend.',
        })
      }
      if (val.vervolgactie === 'KOSTEN_VERREKENEN' && nietGoed) {
        if (val.vervolgactieBedrag === undefined || val.vervolgactieBedrag === null || val.vervolgactieBedrag <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['vervolgactieBedrag'],
            message: 'Bedrag is verplicht bij kosten verrekenen (groter dan 0).',
          })
        }
        if (!val.vervolgactieReden || val.vervolgactieReden.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['vervolgactieReden'],
            message: 'Reden is verplicht bij het verrekenen van schade.',
          })
        }
      }
      if (val.voorraadActie === 'TERUG_OP_VOORRAAD' && nietGoed) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['voorraadActie'],
          message: 'Een beschadigd of onvolledig item gaat niet terug op voorraad.',
        })
      }
      return
    }
    if (!val.vervolgactie) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['vervolgactie'],
        message: 'Kies een vervolgactie voor een niet-ingeleverd item.',
      })
    }
    if (!val.vervolgactieReden || val.vervolgactieReden.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['vervolgactieReden'],
        message: 'Reden is verplicht bij een niet-ingeleverd item.',
      })
    }
    if (
      val.vervolgactie === 'KOSTEN_VERREKENEN' &&
      (val.vervolgactieBedrag === undefined || val.vervolgactieBedrag === null || val.vervolgactieBedrag <= 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['vervolgactieBedrag'],
        message: 'Bedrag is verplicht bij kosten verrekenen (groter dan 0).',
      })
    }
  })
export type InnameItemInput = z.infer<typeof innameItemSchema>

/** PNG data-URL uit het handtekening-canvas. */
const innameHandtekening = z
  .string()
  .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'Handtekening ontbreekt of is ongeldig')
  .max(2_000_000)

export const innameRegistrerenSchema = z
  .object({
  /** Bestaande sessie voortzetten; leeg = nieuwe innamesessie. */
  sessieId: idParam('sessieId').optional().nullable(),
  /** ISO-datum van de inname (default vandaag, Europe/Amsterdam). */
  datum: isoDag.optional().nullable(),
  /** Ontvangen door (default: de ingelogde gebruiker). */
  ontvangenDoorEmployeeId: idParam('ontvangenDoorEmployeeId').optional().nullable(),
  plek: z.string().trim().max(200).optional().nullable(),
  opmerking: z.string().trim().max(2000).optional().nullable(),
  items: z.array(innameItemSchema).min(1, 'Vink minimaal een item aan.'),
  /**
   * batch-67 (FO-499 §5.3) — de medewerker tekent voor wat hij inlevert:
   * verplicht zodra er minstens één item als ingeleverd wordt geregistreerd.
   */
  handtekeningAfgever: innameHandtekening.optional().nullable(),
  /** Handtekening van wie de spullen in ontvangst neemt (optioneel). */
  handtekeningOntvanger: innameHandtekening.optional().nullable(),
  /** Idempotentie van een (offline gebufferde) verzending. */
  idempotencyKey: z.string().trim().min(8).max(200).optional().nullable(),
  /**
   * batch-67 (FO-505 §C10) - is de eindafrekening al definitief en verandert
   * deze inname een verrekenbedrag, dan is de reden van de nieuwe versie verplicht
   * (de server stelt die versie daarna direct op).
   */
  nieuweVersieReden: z.string().trim().max(2000).optional().nullable(),
  })
  .superRefine((val, ctx) => {
    if (val.items.some((i) => i.ingeleverd) && !val.handtekeningAfgever) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['handtekeningAfgever'],
        message: 'De medewerker die inlevert moet tekenen voor de inname.',
      })
    }
  })
export type InnameRegistrerenInput = z.infer<typeof innameRegistrerenSchema>

export const inleverbriefGenererenSchema = z.object({
  inleverDatum: isoDag.optional().nullable(),
  inleverPlek: z.string().trim().max(300).optional().nullable(),
  inleverPersoon: z.string().trim().max(200).optional().nullable(),
  contactNaam: z.string().trim().max(200).optional().nullable(),
  contactEmail: z.string().trim().email('Ongeldig e-mailadres').max(200).optional().nullable().or(z.literal('')),
  contactTelefoon: z.string().trim().max(60).optional().nullable(),
  /** Template-tekst "gevolgen bij niet inleveren"; leeg = de STREEP-standaardtekst. */
  gevolgenTekst: z.string().trim().max(4000).optional().nullable(),
})
export type InleverbriefGenererenInput = z.infer<typeof inleverbriefGenererenSchema>

export const offboardingDocumentVerstuurSchema = z.object({
  /** Te versturen versie (default: de laatste). */
  versieId: idParam('versieId').optional().nullable(),
  /** Optionele persoonlijke toelichting in de mail. */
  bericht: z.string().trim().max(2000).optional().nullable(),
})
export type OffboardingDocumentVerstuurInput = z.infer<typeof offboardingDocumentVerstuurSchema>

export const innameBevestigingSchema = z.object({
  sessieId: idParam('sessieId'),
  bericht: z.string().trim().max(2000).optional().nullable(),
  /** Termijn (dagen) voor de missende zaken, default 14. */
  termijnDagen: z.number().int().min(1).max(90).default(14),
})
export type InnameBevestigingInput = z.infer<typeof innameBevestigingSchema>

export const eindafrekeningAkkoordSchema = z.object({
  versieId: idParam('versieId'),
  rol: z.enum(OFFBOARDING_AKKOORD_ROLLEN),
  toelichting: z.string().trim().max(2000).optional().nullable(),
})
export type EindafrekeningAkkoordInput = z.infer<typeof eindafrekeningAkkoordSchema>

export const stapOverslaanSchema = z.object({
  stapKey: z.string().trim().min(1).max(200),
  reden: z.string().trim().min(3, 'Reden is verplicht bij overslaan (minimaal 3 tekens).').max(2000),
  variant: z.enum(OFFBOARDING_OVERSLAG_VARIANTEN).optional().nullable(),
})
export type StapOverslaanInput = z.infer<typeof stapOverslaanSchema>

export const stapOverslagOngedaanSchema = z.object({
  stapKey: z.string().trim().min(1).max(200),
  reden: z.string().trim().min(3, 'Reden is verplicht.').max(2000),
})
export type StapOverslagOngedaanInput = z.infer<typeof stapOverslagOngedaanSchema>

// ─── batch-71 offboarding-inname (FO-535): al ingenomen, bulk en de NAW-mails ─

/**
 * Knop "Al ingenomen, geen mutatie nodig" (§A4), per regel en als bulk. Met
 * `dryRun` komt alleen de preview terug: per regel wat er gaat gebeuren.
 */
export const alIngenomenSchema = z.object({
  bronSubKeys: z.array(z.string().trim().min(1).max(200)).min(1, 'Kies minimaal een regel.').max(500),
  /** Staat voor alle regels (default GOED). Bij een nieuwe boeking vraagt een andere staat een foto: dat kan alleen op het innamescherm. */
  staat: z.enum(OFFBOARDING_INLEVER_STATEN).optional().nullable(),
  /** Eén gezamenlijke toelichting; leeg = "Fysiek al ingenomen vóór/buiten de offboarding". */
  toelichting: z.string().trim().max(2000).optional().nullable(),
  /** Waar nageboekt uniek bezit en bulk terugkomen; leeg = het eerste magazijn. */
  locatieId: idParam('locatieId').optional().nullable(),
  dryRun: z.boolean().optional(),
})
export type AlIngenomenInput = z.infer<typeof alIngenomenSchema>

/** Standaardtoelichting bij "Al ingenomen". */
export const AL_INGENOMEN_STANDAARD_TOELICHTING = 'Fysiek al ingenomen vóór of buiten de offboarding'

/** Wat "Al ingenomen" met een regel doet (preview en resultaat). */
export const AL_INGENOMEN_UITKOMSTEN = ['LEDGER_BEKEND', 'WORDT_NAGEBOEKT', 'GEEN_BOEKING', 'GEWEIGERD'] as const
export type AlIngenomenUitkomst = (typeof AL_INGENOMEN_UITKOMSTEN)[number]
export const AL_INGENOMEN_UITKOMST_LABELS: Record<AlIngenomenUitkomst, string> = {
  LEDGER_BEKEND: 'Staat al in het register',
  WORDT_NAGEBOEKT: 'Wordt nageboekt',
  GEEN_BOEKING: 'Geen boeking nodig',
  GEWEIGERD: 'Geweigerd',
}

export interface AlIngenomenRegelDTO {
  bronSubKey: string
  omschrijving: string
  uitkomst: AlIngenomenUitkomst
  /** Uitleg in gewone taal: de ledger-regel, wat er wordt nageboekt of waarom het niet kan. */
  uitleg: string
}

export interface AlIngenomenResultaatDTO {
  dryRun: boolean
  regels: AlIngenomenRegelDTO[]
  /** Tellingen voor de knop: "6 worden nageboekt". */
  ledgerBekend: number
  nageboekt: number
  geenBoeking: number
  geweigerd: number
  /** Regels die niet konden worden verwerkt terwijl de preview ze goedkeurde. */
  fouten: Array<{ bronSubKey: string; message: string }>
  /** Waar nageboekt uniek bezit en bulk terechtkomen; null = geen locatie gevonden. */
  locatieNaam: string | null
}

/** Bulk overslaan (§A5): dezelfde reden voor meerdere inname-regels. */
export const stappenBulkOverslaanSchema = z.object({
  stapKeys: z.array(z.string().trim().min(1).max(200)).min(1, 'Kies minimaal een regel.').max(500),
  reden: z.string().trim().min(3, 'Reden is verplicht bij overslaan (minimaal 3 tekens).').max(2000),
})
export type StappenBulkOverslaanInput = z.infer<typeof stappenBulkOverslaanSchema>

/** HR beslist over een conflict tussen cockpit en innamelijst (§6.6). */
export const INNAME_CONFLICT_BESLUITEN = ['INGELEVERD', 'COCKPIT_BLIJFT'] as const
export type InnameConflictBesluit = (typeof INNAME_CONFLICT_BESLUITEN)[number]
export const INNAME_CONFLICT_BESLUIT_LABELS: Record<InnameConflictBesluit, string> = {
  INGELEVERD: 'Het item is ingeleverd: de innamelijst klopt',
  COCKPIT_BLIJFT: 'Het besluit in de cockpit blijft staan',
}
export const inleverConflictOplossenSchema = z.object({
  besluit: z.enum(INNAME_CONFLICT_BESLUITEN),
  reden: z.string().trim().min(3, 'Reden is verplicht (minimaal 3 tekens).').max(2000),
  /** Haalt dit een verrekenbedrag weg uit een definitieve eindafrekening: de reden van de nieuwe versie. */
  nieuweVersieReden: z.string().trim().max(2000).optional().nullable(),
})
export type InleverConflictOplossenInput = z.infer<typeof inleverConflictOplossenSchema>

/** De knop "Eindafrekening mailen naar salarisadministratie" of "... naar medewerker" (§B6). */
export const eindafrekeningMailenSchema = z.object({
  /** Te versturen versie (default: de laatste). */
  versieId: idParam('versieId').optional().nullable(),
  /** Al verstuurd en toch opnieuw: nieuwe mail, met audit. */
  opnieuw: z.boolean().optional(),
})
export type EindafrekeningMailenInput = z.infer<typeof eindafrekeningMailenSchema>

export interface EindafrekeningMailResultaatDTO {
  versieId: string
  versieNr: number
  /** VERSTUURD, of waarom niet. GEEN_ADRES linkt naar de instelling. */
  status: 'VERSTUURD' | 'GEEN_ADRES' | 'MISLUKT'
  melding: string
}

/**
 * De knop "Afmelden bij salarisadministratie" (§B7). Gaat de poort via de variant
 * BUITEN_SYSTEEM open, dan is de bevestigde aanleverdatum verplicht (de server toetst dat).
 */
export const salarisAfmeldenSchema = z.object({
  toelichting: z.string().trim().max(2000).optional().nullable(),
  /** ISO-dag: de eindafrekening is buiten de app aangeleverd op deze datum. */
  buitenSysteemAangeleverdOp: isoDag.optional().nullable(),
  /** Al verstuurd en toch opnieuw: nieuwe mail, met audit. */
  opnieuw: z.boolean().optional(),
})
export type SalarisAfmeldenInput = z.infer<typeof salarisAfmeldenSchema>

/** De salarisadministratie heeft de afmelding bevestigd (handmatig vastgelegd, §B7). */
export const salarisAfmeldingBevestigenSchema = z.object({
  notitie: z.string().trim().max(2000).optional().nullable(),
})
export type SalarisAfmeldingBevestigenInput = z.infer<typeof salarisAfmeldingBevestigenSchema>

/** `OperationeleTaak.bronSubKey`: de workflow kon niet starten (§C12). */
export const OFFBOARDING_STARTEN_SUBKEY = 'OFFBOARDING_STARTEN'
/** `OperationeleTaak.bronSubKey`: een al verzonden afmelding is aangehouden (§6.4). */
export const SALARIS_AFMELDING_AANGEHOUDEN_SUBKEY = 'SALARIS_AFMELDING_AANGEHOUDEN'
/** `OperationeleTaak.bronSubKey` van de taak "Inname <naam>" (bronModule 'inname-sessie', §C12). */
export const INNAME_SESSIE_TAAK_SUBKEY = 'INNAME_SESSIE_TAAK'

// ─── batch-67 middelen-inname, fase B (FO-505): eindafrekening-verrekeningen ───
//
// De sectie Verrekeningen van de eindafrekening: boetes en overnames (bestaand),
// ontbrekende en beschadigde middelen, verhaalde reparaties en handmatige regels.
// Bedragen staan vanuit de medewerker gezien: negatief = inhouden, positief =
// uitbetalen.

export const EINDAFREKENING_REGEL_CATEGORIEEN = ['KOSTEN', 'VERGOEDING', 'VOORSCHOT', 'OVERIG'] as const
export type EindafrekeningRegelCategorie = (typeof EINDAFREKENING_REGEL_CATEGORIEEN)[number]
export const EINDAFREKENING_REGEL_CATEGORIE_LABELS: Record<EindafrekeningRegelCategorie, string> = {
  KOSTEN: 'Kosten',
  VERGOEDING: 'Vergoeding',
  VOORSCHOT: 'Voorschot',
  OVERIG: 'Overig',
}

export const EINDAFREKENING_REGEL_RICHTINGEN = ['INHOUDEN', 'UITBETALEN'] as const
export type EindafrekeningRegelRichting = (typeof EINDAFREKENING_REGEL_RICHTINGEN)[number]
export const EINDAFREKENING_REGEL_RICHTING_LABELS: Record<EindafrekeningRegelRichting, string> = {
  INHOUDEN: 'Inhouden (ten laste van de medewerker)',
  UITBETALEN: 'Uitbetalen (ten gunste van de medewerker)',
}

export const EINDAFREKENING_VERREKEN_SOORTEN = ['BOETE', 'OVERNAME', 'ONTBREKEND', 'BESCHADIGD', 'REPARATIE', 'HANDMATIG'] as const
export type EindafrekeningVerrekenSoort = (typeof EINDAFREKENING_VERREKEN_SOORTEN)[number]
export const EINDAFREKENING_VERREKEN_SOORT_LABELS: Record<EindafrekeningVerrekenSoort, string> = {
  BOETE: 'Boete',
  OVERNAME: 'Overname',
  ONTBREKEND: 'Niet ingeleverd',
  BESCHADIGD: 'Beschadigd ingeleverd',
  REPARATIE: 'Reparatie',
  HANDMATIG: 'Handmatige regel',
}

/** Eén regel van de sectie Verrekeningen (backoffice: met interne toelichting en actor). */
export interface OffboardingVerrekenRegelSpecDTO {
  /** Stabiele sleutel (soort plus id). */
  sleutel: string
  soort: EindafrekeningVerrekenSoort
  omschrijving: string
  /** Decimal-string EUR, vanuit de medewerker gezien: negatief = inhouden, positief = uitbetalen. */
  bedrag: string
  /** Waar de regel vandaan komt ("Inleverlijst", "Boeteregister", "Reparatieverzoek", ...). */
  bron: string
  toelichting: string | null
  /** Wie de regel vastlegde of het besluit nam. */
  actor: string | null
  /** Alleen bij HANDMATIG. */
  handmatigeRegelId?: string
  categorie?: EindafrekeningRegelCategorie
  heeftBijlage?: boolean
  /** Het verhaalbesluit achter deze regel staat onder bezwaar. */
  onderBezwaar?: boolean
}

export interface OffboardingEindafrekeningBezwaarDTO {
  id: string
  versieNr: number | null
  /** ISO-datetime. */
  ingediendOp: string
  toelichting: string
  afgehandeldOp: string | null
  afgehandeldDoorNaam: string | null
  uitkomst: string | null
}

/** Het verrekeningen-blok in de cockpit (backoffice). */
export interface OffboardingVerrekeningenDTO {
  regels: OffboardingVerrekenRegelSpecDTO[]
  /** Decimal-strings EUR, positieve getallen. */
  totaalVerrekenen: string
  totaalUitTeBetalen: string
  /**
   * Het deel van `totaalUitTeBetalen` dat uit de open declaraties komt. Die staan
   * niet als regel in de lijst (ze hebben hun eigen blok), dus de UI en de export
   * noemen het bedrag apart.
   */
  totaalDeclaraties: string
  /** Uit te betalen min verrekenen; negatief = per saldo inhouden. */
  saldo: string
  /** Van Ysselt-credits van het lopende kwartaal: informatief, geen verrekening. */
  creditsSaldo: { beschikbaar: number; periode: string } | null
  waarschuwing: {
    /** Is er een loonstrook met een nettoloon? Zonder is het percentage niet te bepalen. */
    nettoLoonBekend: boolean
    /** Verrekentotaal als percentage van het laatste nettoloon (1 decimaal), of null. */
    pct: string | null
    drempelPct: string
    overschreden: boolean
    toelichting: string | null
    toelichtingDoorNaam: string | null
  }
  /** De laatste versie is definitief: elke wijziging van een verrekenbedrag vraagt een nieuwe versie met reden. */
  nieuweVersieVereist: boolean
  /**
   * Waarom de workflow nu niet gewoon kan worden afgerond (verrekeningen zonder
   * kloppende definitieve eindafrekening, of een open bezwaar); null = geen blokkade.
   */
  afrondBlokkade: string | null
  /** De ingelogde gebruiker mag handmatige regels beheren. */
  magBeheren: boolean
  bezwaren: OffboardingEindafrekeningBezwaarDTO[]
  onderBezwaar: boolean
  /** Dagen waarbinnen de medewerker bezwaar kan maken (instelling). */
  bezwaartermijnDagen: number
}

export const eindafrekeningRegelToevoegenSchema = z
  .object({
    omschrijving: z.string().trim().min(3, 'Omschrijving is verplicht (minimaal 3 tekens).').max(200),
    richting: z.enum(EINDAFREKENING_REGEL_RICHTINGEN),
    /** Altijd een positief bedrag; de richting bepaalt het teken. */
    bedrag: bedragTekst,
    categorie: z.enum(EINDAFREKENING_REGEL_CATEGORIEEN),
    reden: z.string().trim().min(3, 'Reden is verplicht (minimaal 3 tekens).').max(2000),
    /** Verplicht zodra de laatste eindafrekening al definitief is: waarom er een nieuwe versie komt. */
    nieuweVersieReden: z.string().trim().max(2000).optional().nullable(),
  })
  .superRefine((val, ctx) => {
    if (Number(val.bedrag) <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bedrag'], message: 'Het bedrag moet groter zijn dan 0.' })
    }
  })
export type EindafrekeningRegelToevoegenInput = z.infer<typeof eindafrekeningRegelToevoegenSchema>
export type EindafrekeningRegelToevoegenPayload = z.input<typeof eindafrekeningRegelToevoegenSchema>

export const eindafrekeningRegelVervallenSchema = z.object({
  reden: z.string().trim().min(3, 'Reden is verplicht (minimaal 3 tekens).').max(2000),
  nieuweVersieReden: z.string().trim().max(2000).optional().nullable(),
})
export type EindafrekeningRegelVervallenInput = z.infer<typeof eindafrekeningRegelVervallenSchema>

/** Toelichting bij een verrekentotaal boven de drempel (FO-505 §5.5). */
export const verrekenToelichtingSchema = z.object({
  toelichting: z.string().trim().min(10, 'Licht toe waarom dit bedrag wordt verrekend (minimaal 10 tekens).').max(4000),
})
export type VerrekenToelichtingInput = z.infer<typeof verrekenToelichtingSchema>

export const eindafrekeningBezwaarSchema = z.object({
  toelichting: z.string().trim().min(10, 'Licht je bezwaar toe (minimaal 10 tekens).').max(4000),
})
export type EindafrekeningBezwaarInput = z.infer<typeof eindafrekeningBezwaarSchema>

export const eindafrekeningBezwaarAfhandelenSchema = z.object({
  uitkomst: z.string().trim().min(3, 'Beschrijf de uitkomst van het bezwaar (minimaal 3 tekens).').max(4000),
})
export type EindafrekeningBezwaarAfhandelenInput = z.infer<typeof eindafrekeningBezwaarAfhandelenSchema>

/**
 * Portal: de specificatie van de definitieve eindafrekening (FO-505 §C11). Per
 * regel de toelichting voor de medewerker en de foto's; nooit een kostprijs,
 * inkoopprijs of marge, en geen namen van wie de regel vastlegde.
 */
export interface MijnEindafrekeningRegelDTO {
  sleutel: string
  soort: EindafrekeningVerrekenSoort
  omschrijving: string
  /** Decimal-string EUR: negatief = wordt ingehouden, positief = wordt uitbetaald. */
  bedrag: string
  toelichting: string | null
  /** Bouwstenen voor de foto-proxy; null = geen foto's. */
  fotos: { bron: 'INLEVERITEM' | 'REPARATIE'; id: string; aantal: number } | null
}

export interface MijnEindafrekeningSpecificatieDTO {
  versieNr: number
  /** Het vrijgegeven document (PDF) in "Documenten". */
  documentId: string
  /** ISO-datetime waarop de versie definitief werd. */
  definitiefOp: string | null
  regels: MijnEindafrekeningRegelDTO[]
  /** Decimal-strings EUR, positieve getallen. */
  totaalVerrekenen: string
  totaalUitTeBetalen: string
  /** ISO-dag tot en met wanneer bezwaar mogelijk is; null = termijn verstreken. */
  bezwaarTot: string | null
  bezwaar: { ingediendOp: string; toelichting: string; afgehandeldOp: string | null; uitkomst: string | null } | null
}

// ─── Set compleet maken (FO-505 §12.4) ────────────────────────────────────────

export const SET_AANVULLING_ACTIES = ['UIT_VOORRAAD', 'BESTELLEN'] as const
export type SetAanvullingActie = (typeof SET_AANVULLING_ACTIES)[number]
export const SET_AANVULLING_ACTIE_LABELS: Record<SetAanvullingActie, string> = {
  UIT_VOORRAAD: 'Uit voorraad aanvullen',
  BESTELLEN: 'Bestellen',
}

export interface OffboardingSetAanvullingRegelDTO {
  voorraadArtikelId: string
  artikelNaam: string
  artikelNummer: string | null
  /** Traceerbaar artikel: aanvullen gaat per exemplaar bij de volgende uitgifte, niet als bulkboeking. */
  traceerbaar: boolean
  /** Hoeveel stuks definitief ontbreken of onbruikbaar zijn teruggekomen. */
  aantalOntbrekend: number
  /** Daarvan al aangevuld of besteld. */
  aantalAfgehandeld: number
  /** Vrij op voorraad (alle locaties samen). */
  opVoorraad: number
  locaties: Array<{ id: string; naam: string; aantal: number }>
  voorstel: SetAanvullingActie
  /** Hoort bij het gereedschapspakket van de medewerker (naam), of null. */
  pakketNaam: string | null
  /** De inlever-regels achter dit artikel. */
  items: Array<{
    itemId: string
    omschrijving: string
    aantal: number
    /** Al doorbelast (kosten verrekenen) of bewust niet; null = nog te kiezen. */
    doorbelast: boolean | null
    /**
     * Deze regel is al op een andere manier geregeld (overgenomen tegen
     * restwaarde, een reparatieverzoek, of gewoon in goede staat ingeleverd) en
     * kan hier niet (nog eens) worden doorbelast. De tekst zegt waarom.
     */
    geregeld: string | null
    /** Decimal-string EUR van de vastgelegde verrekening, of null. */
    bedrag: string | null
    waardeVoorstel: OffboardingWaardeVoorstelDTO | null
  }>
  afgehandeld: Array<{
    actie: SetAanvullingActie
    aantal: number
    /** ISO-datetime. */
    op: string
    doorNaam: string | null
    leverancierBestellingId: string | null
  }>
}

export interface OffboardingSetAanvullingDTO {
  regels: OffboardingSetAanvullingRegelDTO[]
  /** Ontbrekende items waarvan het catalogusartikel onbekend is: niet aan te vullen of te bestellen vanuit dit scherm. */
  zonderArtikel: Array<{ itemId: string; omschrijving: string; aantal: number }>
  /** Er valt nog iets aan te vullen of te bestellen. */
  openstaand: boolean
  /** De eindafrekening is al definitief: doorbelasten wijzigen vraagt de reden van een nieuwe versie. */
  nieuweVersieVereist: boolean
}

export const setAanvullingSchema = z.object({
  regels: z
    .array(
      z.object({
        voorraadArtikelId: idParam('voorraadArtikelId'),
        actie: z.enum(SET_AANVULLING_ACTIES),
        aantal: z.number().int().min(1, 'Het aantal is minimaal 1.').max(9999),
        /**
         * Verplicht bij uit voorraad aanvullen van een bulkartikel (de server
         * controleert dat: een artikel dat per exemplaar wordt geregistreerd kent
         * geen bulklocatie).
         */
        locatieId: idParam('locatieId').optional().nullable(),
      }),
    )
    .max(200),
  /** Per inlever-regel: doorbelasten in de eindafrekening, ja of nee (§A3). */
  doorbelasten: z
    .array(
      z
        .object({
          itemId: idParam('itemId'),
          doorbelasten: z.boolean(),
          /** Bedrag bij doorbelasten (EUR); leeg = het waardevoorstel. */
          bedrag: z.number().min(0).max(1_000_000, 'Het bedrag is te hoog.').optional().nullable(),
          reden: z.string().trim().max(2000).optional().nullable(),
        })
        .superRefine((val, ctx) => {
          if (!val.doorbelasten && (!val.reden || val.reden.length < 3)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reden'], message: 'Geef aan waarom dit niet wordt doorbelast.' })
          }
        }),
    )
    .max(500)
    .default([]),
  /**
   * De eindafrekening is al definitief en een doorbelast-keuze verandert een
   * bedrag: de reden van de nieuwe versie (de server stelt die daarna op).
   */
  nieuweVersieReden: z.string().trim().max(2000).optional().nullable(),
})
export type SetAanvullingInput = z.infer<typeof setAanvullingSchema>
export type SetAanvullingPayload = z.input<typeof setAanvullingSchema>
